<div class="generate-article-dialog">
  <div class="dialog-header">
    <h2 mat-dialog-title>Generate article</h2>
    <p class="subtitle">
      @if (initialUserArticleTitle) {
        Your title: <strong>{{ initialUserArticleTitle }}</strong>. Below are AI-suggested titles and settings you can adjust before generating.
      } @else {
        Here are the suggested parameters for your article.
      }
    </p>
    <button mat-icon-button (click)="close()" class="close-btn" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content>
    @if (suggestLoading) {
      <div class="loading-block">
        <mat-spinner diameter="36"></mat-spinner>
        <span>Loading suggestions…</span>
      </div>
    } @else if (suggestError) {
      <div class="error-block">{{ suggestError }}</div>
      <button mat-button (click)="loadSuggestions()">Retry</button>
    } @else {
      <div class="form-sections" [class.form-disabled]="formDisabled">
        <!-- Audience / Location (read-only from project) -->
        <mat-expansion-panel [expanded]="false" class="field-panel" [disabled]="formDisabled">
          <mat-expansion-panel-header>
            <mat-panel-title>Audience / Location</mat-panel-title>
            <mat-panel-description>{{ audienceLocation || 'Set location' }}</mat-panel-description>
          </mat-expansion-panel-header>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Audience / Location</mat-label>
            <input matInput [(ngModel)]="audienceLocation" name="audienceLocation" placeholder="e.g. UK, Global" disabled>
          </mat-form-field>
        </mat-expansion-panel>

        <!-- Title (two tabs) -->
        <mat-expansion-panel [expanded]="false" class="field-panel" [disabled]="formDisabled">
          <mat-expansion-panel-header>
            <mat-panel-title>Title</mat-panel-title>
            <mat-panel-description>{{ selectedTitle || 'Select or type title' }}</mat-panel-description>
          </mat-expansion-panel-header>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Article title</mat-label>
            <input matInput [(ngModel)]="selectedTitle" name="selectedTitle" placeholder="Enter or pick a title" [readonly]="formDisabled">
          </mat-form-field>
          <mat-tab-group [(selectedIndex)]="titleTabIndex" class="title-tabs">
            <mat-tab label="Suggested by AI">
              <div class="suggest-list">
                @for (t of suggestedTitles; track t) {
                  <button type="button" mat-stroked-button class="suggest-item" (click)="pickTitle(t)" [disabled]="formDisabled">{{ t }}</button>
                }
                @if (suggestedTitles.length === 0) { <span class="muted">No suggestions yet.</span> }
              </div>
            </mat-tab>
            <mat-tab label="Used by Competitors">
              <div class="suggest-list">
                @for (t of titlesUsedByCompetitors; track t) {
                  <button type="button" mat-stroked-button class="suggest-item" (click)="pickTitle(t)" [disabled]="formDisabled">{{ t }}</button>
                }
                @if (titlesUsedByCompetitors.length === 0) { <span class="muted">No competitor titles loaded.</span> }
              </div>
            </mat-tab>
          </mat-tab-group>
        </mat-expansion-panel>

        <!-- Keywords -->
        <mat-expansion-panel class="field-panel" [disabled]="formDisabled">
          <mat-expansion-panel-header>
            <mat-panel-title>Keywords</mat-panel-title>
            <mat-panel-description>{{ selectedKeywords.length }} selected</mat-panel-description>
          </mat-expansion-panel-header>
          <div class="chips-row">
            @for (kw of suggestedKeywords; track kw) {
              <button type="button" mat-chip (click)="toggleKeyword(kw)" [class.selected]="isKeywordSelected(kw)" [disabled]="formDisabled">{{ kw }}</button>
            }
          </div>
        </mat-expansion-panel>

        <!-- Word count -->
        <mat-expansion-panel class="field-panel" [disabled]="formDisabled">
          <mat-expansion-panel-header>
            <mat-panel-title>Word count</mat-panel-title>
            <mat-panel-description>{{ wordCount }}</mat-panel-description>
          </mat-expansion-panel-header>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Word count</mat-label>
            <mat-select [(ngModel)]="wordCount" name="wordCount" [disabled]="formDisabled">
              @for (w of wordCountOptions; track w) {
                <mat-option [value]="w">{{ w }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </mat-expansion-panel>

        <!-- Tone -->
        <mat-expansion-panel class="field-panel" [disabled]="formDisabled">
          <mat-expansion-panel-header>
            <mat-panel-title>Tone</mat-panel-title>
            <mat-panel-description>{{ toneOfVoice }}</mat-panel-description>
          </mat-expansion-panel-header>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Tone</mat-label>
            <mat-select [(ngModel)]="toneOfVoice" name="toneOfVoice" [disabled]="formDisabled">
              @for (t of toneOptions; track t) {
                <mat-option [value]="t">{{ t }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </mat-expansion-panel>

        <!-- Internal URL links -->
        <mat-expansion-panel class="field-panel" [disabled]="formDisabled">
          <mat-expansion-panel-header>
            <mat-panel-title>Internal URL links</mat-panel-title>
            <mat-panel-description>{{ selectedInternalLinks.length }} selected</mat-panel-description>
          </mat-expansion-panel-header>
          <div class="link-list">
            @for (link of suggestedInternalLinks; track link.url) {
              <button type="button" mat-stroked-button class="link-item" (click)="toggleInternalLink(link)" [class.selected]="isInternalLinkSelected(link)" [disabled]="formDisabled">
                {{ link.label || link.url }}
              </button>
            }
            @if (suggestedInternalLinks.length === 0) {
              <span class="muted">No internal URLs suggested. Add URLs in project or they will appear when available.</span>
            }
          </div>
        </mat-expansion-panel>

        <!-- CTAs -->
        @if (suggestedCtas.length) {
          <mat-expansion-panel class="field-panel" [disabled]="formDisabled">
            <mat-expansion-panel-header>
              <mat-panel-title>CTAs</mat-panel-title>
              <mat-panel-description>{{ selectedCtas.length }} selected</mat-panel-description>
            </mat-expansion-panel-header>
            <div class="link-list">
              @for (cta of suggestedCtas; track cta.url) {
                <button type="button" mat-stroked-button class="link-item" (click)="toggleCta(cta)" [class.selected]="isCtaSelected(cta)" [disabled]="formDisabled">
                  {{ cta.label || cta.url }}
                </button>
              }
            </div>
          </mat-expansion-panel>
        }

        <!-- FAQs -->
        @if (suggestedFaqs.length) {
          <mat-expansion-panel class="field-panel" [disabled]="formDisabled">
            <mat-expansion-panel-header>
              <mat-panel-title>FAQs in article</mat-panel-title>
              <mat-panel-description>{{ selectedFaqs.length }} selected</mat-panel-description>
            </mat-expansion-panel-header>
            <div class="faq-list">
              @for (faq of suggestedFaqs; track faq.question) {
                <button type="button" mat-stroked-button class="faq-item" (click)="toggleFaq(faq)" [class.selected]="isFaqSelected(faq)" [disabled]="formDisabled">
                  {{ faq.question }}
                </button>
              }
            </div>
          </mat-expansion-panel>
        }

        <!-- AI images – commented out (image generation disabled) -->
        <!-- <mat-expansion-panel class="field-panel">
          <mat-expansion-panel-header>
            <mat-panel-title>AI images</mat-panel-title>
            <mat-panel-description>{{ withAiGeneratedImages ? 'Yes' : 'No' }}</mat-panel-description>
          </mat-expansion-panel-header>
          <mat-checkbox [(ngModel)]="withAiGeneratedImages" name="withAiGeneratedImages">
            Include AI-generated images in the article
          </mat-checkbox>
        </mat-expansion-panel> -->
      </div>

      @if (generateError) {
        <div class="error-block">{{ generateError }}</div>
      }

      @if (generatedArticleId) {
        <div class="success-block">
          <p>Article generated successfully.</p>
          <button mat-flat-button color="primary" (click)="showArticle()">Show article</button>
        </div>
      } @else {
        <div class="actions">
          <button mat-flat-button color="primary" (click)="generate()" [disabled]="generateLoading || !(selectedTitle && selectedTitle.trim())">
            @if (generateLoading) {
              <mat-spinner diameter="20" class="btn-spinner"></mat-spinner>
              Generating…
            } @else {
              Generate article
            }
          </button>
        </div>
      }
    }
  </mat-dialog-content>
</div>
